import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import {
  getReservationStockinItem,
  stockInReservationItem,
} from "../../../services/OtherService";
import { RequisitionStatusBadge } from "../../components/Requisition";
import { validateProductBarcode } from "../../../services/ProductService";
import { searchReservation, searchRack } from "../../../services/RackService";
import Select from "react-select";

const EmptyStocks = {
  barcode_no: "",
  rack: null,
  isRackDisable: false,
  isValid: false,
};
const LoadingIndicator = () => {
  return <Spinner color="primary" size={"sm"} delay={0} />;
};

const ReservationStockIn = forwardRef(
  ({ toggleModal, setIsSubmitLoading }, ref) => {
    const [reservationNo, setReservationNo] = useState(null);
    const [reservationOption, setReservationOption] = useState([]);
    const [isReservationNoLoading, setIsReservationNoLoading] = useState(false);
    const [reservationData, setReservationData] = useState(null);
    const [reservationItems, setReservationItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [optionRack, setOptionRack] = useState([]);
    const [isRackLoading, setIsRackLoading] = useState(false);

    useEffect(() => {
      const fetchReservationOptions = async () => {
        try {
          setIsReservationNoLoading(true);
          const response = await searchReservation();
          setReservationOption(response.data.data || []);
        } catch (error) {
          if (error.response && error.response.data.message) {
            toast.error(error.response.data.message);
          } else {
            toast.error("Failed to fetch reservation options");
          }
        } finally {
          setIsReservationNoLoading(false);
        }
      };
      fetchReservationOptions();
      fetchRackOptions();
    }, []);

    const fetchReservationData = async (reqNo) => {
      if (!reqNo) {
        toast.error("Reservation No is required");
        return;
      }
      try {
        setIsLoading(true);
        const response = await getReservationStockinItem(reqNo);
        const { reservation, items } = response.data.data || {};
        setReservationData(reservation);
        setReservationItems(
          items.map((item) => ({
            ...item,
            quantity: item.quantity || 0,
            stocks: [EmptyStocks],
          })) || []
        );
      } catch (error) {
        if (error.response && error.response.data) {
          toast.error(
            error.response.data.message || "Failed to fetch reservation data"
          );
        } else {
          console.error("Error fetching reservation data:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    async function fetchRackOptions() {
      try {
        setIsRackLoading(true);
        const resp = await searchRack();
        setOptionRack(resp.data.data);
      } catch (error) {
        console.log("ERROR => ", error);
      } finally {
        setIsRackLoading(false);
      }
    }

    const handleReservationNoChange = (data) => {
      setReservationNo(data);
      if (data?.value) {
        fetchReservationData(data.value);
      }
      setReservationData(null);
      setReservationItems([]);
    };

    const handleReservationItemStockChange = (index, stockIndex, stockData) => {
      setReservationItems((prevItems) =>
        prevItems.map((item, i) => {
          if (i !== index) return item;
          const stocks = item.stocks.map((stock, j) =>
            j === stockIndex ? { ...stock, ...stockData } : stock
          );
          const isExistEmptyStock = stocks.some(
            (stock) => stock.isValid === false
          );
          const isFulfilled = item.product.is_multi_unit
            ? true
            : stocks.length === item.quantity;
          let newStocks = stocks;
          if (!isFulfilled && !isExistEmptyStock) {
            newStocks = [...stocks, EmptyStocks];
          }
          return { ...item, stocks: newStocks };
        })
      );
    };

    const handleBarcodeChange = (value, index, stockIndex) => {
      const stockData = {
        barcode_no: value,
        rack: null,
        isRackDisable: false,
        isValid: false,
      };
      handleReservationItemStockChange(index, stockIndex, stockData);
    };

    const handleRackChange = (data, index, stockIndex) => {
      const elem = document.getElementById(`rack-${index}-${stockIndex}`);
      handleReservationItemStockChange(index, stockIndex, { rack: data });
      elem?.classList.remove("is-invalid");
    };

    const hasDuplicatesBarcode = () => {
      const seen = new Set();
      for (const item of reservationItems) {
        for (const stock of item.stocks) {
          if (stock.barcode_no && seen.has(stock.barcode_no)) {
            return true;
          }
          seen.add(stock.barcode_no);
        }
      }
      return false;
    };

    const getPreviousSelectedRack = (itemIndex, stockIndex) => {
      if (stockIndex <= 0) return null;
      const item = reservationItems[itemIndex];
      const previousStock = item?.stocks?.[stockIndex - 1];
      return previousStock?.rack || null;
    };

    const getBarcodeStock = async (e, index, stockIndex) => {
      if (e.key === "Enter" && e.target.value.length > 0) {
        if (hasDuplicatesBarcode()) {
          toast.error("Barcode already exists in stock in list");
        } else {
          const elem = document.getElementById(
            `barcode_no-${index}-${stockIndex}`
          );
          try {
            const product_id = reservationItems[index].product?.id;
            const resp = await validateProductBarcode(
              e.target.value,
              product_id
            );
            const existingRack = resp.data?.existing_rack;
            let stockData;
            if (existingRack) {
              stockData = {
                isValid: true,
                isRackDisable: true,
                rack: existingRack,
              };
            } else {
              stockData = {
                isValid: true,
                isRackDisable: false,
                rack: getPreviousSelectedRack(index, stockIndex),
              };
            }
            handleReservationItemStockChange(index, stockIndex, stockData);
            elem?.classList.remove("is-invalid");
          } catch (error) {
            elem?.classList.add("is-invalid");
            toast.error(error.response.data.message);
          }
        }
      }
    };

    const validateReservationItems = () => {
      for (const item of reservationItems) {
        for (const stock of item.stocks) {
          if (!stock.isValid) {
            toast.error("Please fill all required fields");
            return false;
          }
          if(stock.rack === null || stock.rack === undefined) {
            toast.error("Please select a rack for all stocks");
            return false;
          }
        }
      }
      return true;
    };

    const handleStockIn = async () => {
      if (!validateReservationItems()) {
        return;
      }
      setIsSubmitLoading(true);
      try {
        const itemList = [];
        reservationItems.forEach((item) => {
          const stockList = [];
          item.stocks.forEach((stock) => {
            if (stock.isValid) {
              stockList.push({
                barcode_no: stock.barcode_no,
                rack_id: stock.rack?.value,
                expired_date: stock.expired_date || null,
              });
            }
          });
          if (stockList.length > 0) {
            itemList.push({
              reservation_item_id: item.id,
              stock_list: stockList,
            });
          }
        });
        const payload = { reservation_items: itemList };
        const reservationId = reservationNo?.value;
        await stockInReservationItem(reservationId, payload);
        toast.success("Stock out successful");
        setIsSubmitLoading(false);
        toggleModal();
      } catch (error) {
        toast.error("Stock out failed");
      } finally {
        setIsSubmitLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      submit: handleStockIn,
    }));

    return (
      <>
        <div>
          <div className="card">
            <div className="d-flex flex-inline align-items-center justify-content-between px-3 py-2">
              <div className="d-flex align-items-center justify-content-start gap-2">
                <Select
                  isClearable={false}
                  value={reservationNo}
                  isDisabled={isLoading}
                  className="react-select"
                  classNamePrefix="select"
                  placeholder="Select Reservation"
                  options={reservationOption}
                  isLoading={isReservationNoLoading}
                  onChange={(data) => {
                    handleReservationNoChange(data);
                  }}
                  components={{ LoadingIndicator }}
                />
              </div>
              {reservationData && (
                <div className="d-flex align-items-center justify-content-between gap-5 flex-wrap">
                  <div className="d-flex flex-column align-items-start">
                    <span>Reserved By</span>
                    <strong>{reservationData.reserved_by || "N/A"}</strong>
                  </div>

                  <div className="d-flex flex-column align-items-start">
                    <span>Reservation Type</span>
                    <strong>{reservationData.reservation_type || "N/A"}</strong>
                  </div>

                  <div className="d-flex flex-column align-items-start">
                    <span>Status</span>
                    <RequisitionStatusBadge status={reservationData.status} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {reservationItems.map((item, index) => (
            <div className="card px-3  py-2 mb-3" key={index}>
              <div className="d-flex flex-wrap align-items-start gap-3">
                <div
                  style={{ backgroundColor: "#e5fff0" }}
                  className="d-flex justify-content-start gap-4 align-items-center mb-2  p-2 rounded"
                >
                  <div className="d-flex flex-column align-items-start">
                    <span className="text-muted">Product:</span>
                    <span className="fw-bold">{item.product.name}</span>
                  </div>

                  <div className="d-flex flex-column align-items-start">
                    <span className="text-muted">Source:</span>
                    <span className="fw-bold">{item.source?.name}</span>
                  </div>

                  <div className="d-flex flex-column align-items-start">
                    <span className="text-muted">Reserved:</span>
                    <span className="badge badge-sm light badge-secondary">
                      {item.quantity} Quantity
                    </span>
                  </div>
                </div>

                <div className="d-flex align-items-center flex-wrap gap-2">
                  {item.stocks.map((stock, stockIndex) => (
                    <div
                      style={{ backgroundColor: "#ededed" }}
                      key={`${index}-${stockIndex}`}
                      className="d-flex align-items-start flex-wrap gap-3 p-2 rounded"
                    >
                      <div className="d-flex flex-colummn gap-2 align-items-center">
                        <span>Barcode</span>
                        <input
                          type="text"
                          id={`barcode_no-${index}-${stockIndex}`}
                          style={{ width: "10rem" }}
                          autoFocus={stock.barcode_no.length === 0}
                          value={stock.barcode_no}
                          onChange={(e) => {
                            handleBarcodeChange(
                              e.target.value,
                              index,
                              stockIndex
                            );
                          }}
                          onKeyPress={(e) => {
                            getBarcodeStock(e, index, stockIndex);
                          }}
                          className="form-control"
                          placeholder="Barcode"
                        />
                      </div>
                      {stock.isValid && (
                        <>
                          <div className="d-flex gap-2 align-items-center">
                            <span>Rack</span>
                            <Select
                              value={stock.rack}
                              onChange={(data) =>
                                handleRackChange(data, index, stockIndex)
                              }
                              placeholder={"Select Rack"}
                              isClearable={false}
                              isLoading={isRackLoading}
                              options={optionRack}
                              id={`rack-${index}-${stockIndex}`}
                              className="react-select"
                              classNamePrefix="select"
                              styles={{
                                container: (provided) => ({
                                  ...provided,
                                  width: "12rem", // Set the width of the select container
                                }),
                              }}
                            />
                          </div>
                          {item.product.perishable_product && (
                            <div className="d-flex flex-column align-itemm-start">
                              <span>
                                Rack : <strong>{stock.rack || "N/A"}</strong>
                              </span>
                              <span>
                                Net Stock :{" "}
                                <strong>{stock.net_quantity || "N/A"}</strong>
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }
);

export default ReservationStockIn;
